import { useEffect } from "react";
import EnquiryForm, {
  type EnquiryIntent,
  type EnquiryKind,
  type EnquiryOption,
} from "./EnquiryForm";

interface EnquiryModalProps {
  open: boolean;
  onClose: () => void;
  kind: EnquiryKind;
  options: EnquiryOption[];
  defaultValue: string;
  intent?: EnquiryIntent;
}

const ARIA_LABEL: Record<EnquiryKind, string> = {
  experience: "Enquire about this experience",
  farm: "Enquire about this farm",
  farmer: "Enquire about this farmer",
  trip: "Enquire about building a custom itinerary",
};

export default function EnquiryModal({
  open,
  onClose,
  kind,
  options,
  defaultValue,
  intent = "enquire",
}: EnquiryModalProps) {
  useEffect(() => {
    if (!open) return undefined;

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto p-4 md:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={intent === "notify" ? "Get notified when this farm reopens" : ARIA_LABEL[kind]}
    >
      <div className="fixed inset-0 bg-foreground-950/70" onClick={onClose} aria-hidden="true"></div>

      <div className="relative w-full max-w-2xl my-4 md:my-0">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close enquiry form"
          className="absolute -top-3 -right-3 md:-top-4 md:-right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-background-50 text-foreground-900 hover:bg-background-200 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="max-h-[86vh] overflow-y-auto rounded-lg">
          <EnquiryForm
            kind={kind}
            options={options}
            defaultValue={defaultValue}
            intent={intent}
          />
        </div>
      </div>
    </div>
  );
}